import { computed, nextTick, ref, toValue, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import { useNotificationHelper } from '@common/core-layer/utils/toast.utils'
import { useVoiceRecorderLabels, VOICE_RECORDER_LABEL_KEYS } from '@common/media-layer/composables/useVoiceRecorderLabels'
import { useDRMUpload } from '@common/media-layer/composables/useDRMUpload'
import { applyOEmbedMeta, fetchOEmbedMeta } from '@common/media-layer/utils/media-embed.utils'
import { useCommunitiesDRMConfig } from '@communities/common-layer/utils/drm-config'
import { useUpload } from '@communities/groups-layer/composables/useMediaUpload'
import { useVoiceRecorder } from '@common/media-layer/composables/useVoiceRecorder'
import { capturePreparedVoiceNote, isVoiceNoteAudioFile, prepareUploadedVoiceNote } from '@common/media-layer/utils/voice-note'
import type { DroppedFile } from '@common/shared-ui-layer/types/drag-drop.types'

import { MessageStatus } from '../types/chats.enums'
import {
  buildEditorValidations,
  buildFileAttachments,
  buildGifAttachments,
  buildMediaAttachments,
  getMediaItemByTypeAndIndex,
  mapFileForCarousel,
  mapGifForCarousel,
  mapImageForCarousel,
  mapVideoForCarousel,
  resolveMediaDimensions,
} from '../utils/chat-message-editor.helpers'

import type {
  ChatMessageEditorOptions,
  EditorFileItem,
  EditorGifMedia,
  EditorMediaItem,
  FileSelection,
  MediaFile,
  RecordedVoiceMessage,
} from '../types/chat-message-editor.types'
import type { IChatAttachment } from '../types/conversation.types'

const DEFAULT_DRM_SOURCE_ENTITY_ID = 'temp'

export const useChatMessageEditor = (options: ChatMessageEditorOptions) => {
  const { t } = useI18n()
  const notification = useNotificationHelper()

  const voiceRecorderLabels = useVoiceRecorderLabels(t, VOICE_RECORDER_LABEL_KEYS.chats)
  // Keep draft editor state local: it is instance-scoped, transient, and includes File/blob data.
  const mediaModel = ref<EditorMediaItem[]>([])
  const gifsModel = ref<EditorGifMedia[]>([])
  const filesModel = ref<EditorFileItem[]>([])
  const isUploading = ref(false)
  const isFileUploading = ref(false)
  const showToolsPopover = ref(false)
  const showGifPicker = ref(false)
  const showGifSheet = ref(false)
  const showVideoUploadModal = ref(false)
  const currentUploadItem = ref<EditorMediaItem | null>(null)

  const revokeBlobUrlIfNeeded = (url?: string) => {
    if (url?.startsWith('blob:')) {
      URL.revokeObjectURL(url)
    }
  }

  const validations = computed(() =>
    buildEditorValidations({
      maxImages: toValue(options.maxImages),
      maxVideos: toValue(options.maxVideos),
      maxFiles: toValue(options.maxFiles),
    })
  )

  const resolvedLocationId = computed(() => toValue(options.locationId) || '')
  const resolvedGroupId = computed(() => toValue(options.groupId) || '')
  const { drmAppSource, drmEntityType, drmSourceEntityType, drmEntityId, drmSourceEntityId } = useCommunitiesDRMConfig(
    resolvedLocationId,
    DEFAULT_DRM_SOURCE_ENTITY_ID
  )

  const {
    uploadToDRM,
    componentRef: DRMComponent,
    currentFile: currentDRMFile,
    drmFileUploadRef,
    handleDRMSuccess,
    handleDRMError,
    handleDRMProgress,
    handleDRMStatusChange,
    cancelUpload,
    playRaw: drmPlayRaw,
  } = useDRMUpload({
    onProgress: progress => {
      if (currentUploadItem.value) {
        currentUploadItem.value.progress = progress
      }
    },
    onSuccess: result => {
      if (currentUploadItem.value) {
        // Prefer CDN/http URL for payloads; processedUrl may be gs:// and is not client-consumable.
        const resolvedUrl = result.url || result.processedUrl || ''
        currentUploadItem.value.licenseId = result.licenseId
        currentUploadItem.value.status = MessageStatus.COMPLETED
        if (resolvedUrl) {
          currentUploadItem.value.url = resolvedUrl
        }
        currentUploadItem.value.id = result.licenseId || resolvedUrl
        currentUploadItem.value = null
        isUploading.value = false
        drmSourceEntityId.value = DEFAULT_DRM_SOURCE_ENTITY_ID
      }
    },
    onError: error => {
      if (currentUploadItem.value) {
        currentUploadItem.value.status = MessageStatus.FAILED
        currentUploadItem.value.error = error instanceof Error ? error.message : 'Upload failed'
        currentUploadItem.value = null
        isUploading.value = false
        drmSourceEntityId.value = DEFAULT_DRM_SOURCE_ENTITY_ID
      }
    },
    playRaw: true,
  })

  const imageCount = computed(() => mediaModel.value.filter(media => media.type === 'image').length)
  const videoCount = computed(() => mediaModel.value.filter(media => media.type === 'video').length)
  const fileCount = computed(() => filesModel.value.length)

  const imagesForCarousel = computed(() => mediaModel.value.filter(media => media.type === 'image').map(mapImageForCarousel))

  const videosForCarousel = computed(() => mediaModel.value.filter(media => media.type === 'video').map(mapVideoForCarousel))

  const gifsForCarousel = computed(() => gifsModel.value.map(mapGifForCarousel))

  const filesForCarousel = computed(() => filesModel.value.map(mapFileForCarousel))

  // --- Voice notes --------------------------------------------------------
  // The editor only records and hands the clip to the parent; optimistic insert,
  // upload, and socket send live in useChats so the bubble appears in history
  // immediately (like every other message) while uploading.
  const voiceRecorder = useVoiceRecorder()
  /** Last user-facing reason an uploaded audio attach was rejected (duration, type, etc.). */
  const lastVoiceAttachError = ref<string | null>(null)

  const hasAttachments = computed(() => {
    return (
      imagesForCarousel.value.length > 0 ||
      videosForCarousel.value.length > 0 ||
      gifsForCarousel.value.length > 0 ||
      filesForCarousel.value.length > 0
    )
  })

  const hasUploadingMedia = computed(() => {
    return (
      mediaModel.value.some(media => media.status === MessageStatus.UPLOADING) ||
      gifsModel.value.some(gif => gif.uploading === true) ||
      filesModel.value.some(file => file.status === MessageStatus.UPLOADING)
    )
  })

  // Queue driver: starts uploads on enqueue; deep mode is required because item status/progress mutates in place.
  watch(
    mediaModel,
    () => {
      if (!isUploading.value) {
        processNextUpload()
      }
    },
    { deep: true }
  )

  watch(isUploading, (newValue, oldValue) => {
    // Resume queue when current upload transitions to idle (including cancel/remove flows).
    if (oldValue && !newValue) {
      processNextUpload()
    }
  })

  watch(
    filesModel,
    () => {
      if (!isFileUploading.value) {
        processNextFileUpload()
      }
    },
    { deep: true }
  )

  watch(isFileUploading, (newValue, oldValue) => {
    if (oldValue && !newValue) {
      processNextFileUpload()
    }
  })

  const processNextUpload = async () => {
    const nextItem = mediaModel.value.find(item => item.status === MessageStatus.PENDING)
    if (!nextItem || isUploading.value) return

    if (!resolvedLocationId.value) {
      nextItem.status = MessageStatus.FAILED
      nextItem.error = t('chats.messageEditor.errors.locationIdRequired')
      isUploading.value = false
      currentUploadItem.value = null
      return
    }

    if (!nextItem.file || nextItem.isImported) {
      nextItem.status = MessageStatus.COMPLETED
      isUploading.value = false
      currentUploadItem.value = null
      processNextUpload()
      return
    }

    isUploading.value = true
    nextItem.status = MessageStatus.UPLOADING
    nextItem.progress = 0
    currentUploadItem.value = nextItem

    drmSourceEntityId.value = nextItem._id

    try {
      await uploadToDRM(nextItem.file)
    } catch (error) {
      nextItem.status = MessageStatus.FAILED
      nextItem.error = error instanceof Error ? error.message : 'Upload failed'
      isUploading.value = false
      currentUploadItem.value = null
      drmSourceEntityId.value = DEFAULT_DRM_SOURCE_ENTITY_ID
    }
  }

  const processNextFileUpload = async () => {
    const nextFile = filesModel.value.find(file => file.status === MessageStatus.PENDING)
    if (!nextFile || isFileUploading.value) return

    if (!resolvedLocationId.value) {
      nextFile.status = MessageStatus.FAILED
      nextFile.error = t('chats.messageEditor.errors.locationIdRequired')
      isFileUploading.value = false
      return
    }

    if (!resolvedGroupId.value) {
      nextFile.status = MessageStatus.FAILED
      nextFile.error = t('chats.messageEditor.errors.groupIdRequired')
      isFileUploading.value = false
      return
    }

    isFileUploading.value = true
    nextFile.status = MessageStatus.UPLOADING
    nextFile.progress = 0

    let stopProgressWatch: (() => void) | null = null
    let stopWatching: (() => void) | null = null
    const stopUploadWatchers = () => {
      stopWatching?.()
      stopProgressWatch?.()
      stopWatching = null
      stopProgressWatch = null
    }

    try {
      const { loadFiles, uploadResponse, uploading, uploadProgress } = useUpload()

      stopProgressWatch = watch(uploadProgress, progress => {
        nextFile.progress = progress
      })

      stopWatching = watch([uploading, uploadResponse], ([isUploadingNow, response], previousState) => {
        const wasUploading = previousState?.[0] ?? false

        if (!isUploadingNow && response) {
          nextFile.serverId = response.id
          nextFile.serverUrl = response.url
          nextFile.status = MessageStatus.COMPLETED

          revokeBlobUrlIfNeeded(nextFile.url)
          nextFile.url = response.url

          isFileUploading.value = false
          stopUploadWatchers()
          return
        }

        // useUpload may fail asynchronously without surfacing an awaitable rejection.
        if (wasUploading && !isUploadingNow && !response) {
          nextFile.status = MessageStatus.FAILED
          nextFile.error = nextFile.error || 'Upload failed'
          isFileUploading.value = false
          stopUploadWatchers()
        }
      })

      await loadFiles(nextFile.file, 'documents', resolvedLocationId.value, resolvedGroupId.value)
    } catch (error) {
      stopUploadWatchers()
      nextFile.status = MessageStatus.FAILED
      nextFile.error = error instanceof Error ? error.message : 'Upload failed'
      isFileUploading.value = false
    }
  }

  /** Stop recording; return clip for optimistic send (upload happens in useChats). */
  const stopVoiceRecordingForSend = async (): Promise<RecordedVoiceMessage | null> => {
    const note = await capturePreparedVoiceNote(() => voiceRecorder.stop())
    return note
  }

  /** Build uploaded-audio payload for the same optimistic send path as mic recordings. */
  const attachUploadedVoiceForSend = async (file: File, localUrl?: string): Promise<RecordedVoiceMessage | null> => {
    lastVoiceAttachError.value = null
    if (voiceRecorder.stage.value !== 'idle') {
      lastVoiceAttachError.value = t('chats.voiceNote.recordingInProgress')
      return null
    }
    const prepared = await prepareUploadedVoiceNote(file, { localUrl })
    if (!prepared.ok) {
      const errorByCode: Record<typeof prepared.code, string> = {
        too_large: t('chats.voiceNote.fileTooLarge'),
        too_long: t('chats.voiceNote.durationTooLong'),
        unsupported: t('chats.voiceNote.unsupportedAudioType'),
        failed: t('chats.voiceNote.uploadFailed'),
      }
      lastVoiceAttachError.value = errorByCode[prepared.code]
      return null
    }

    return prepared.note
  }

  const showVoiceAttachError = () => {
    if (!lastVoiceAttachError.value) return
    notification.showError({ title: t('chats.voiceNote.uploadAudio'), description: lastVoiceAttachError.value })
  }

  const emitVoiceMessage = (recorded: RecordedVoiceMessage) => {
    options.onVoiceMessage?.(recorded)
  }

  const emitVoiceFromFiles = async (files: FileSelection[]) => {
    for (const selection of files) {
      const recorded = await attachUploadedVoiceForSend(selection.file, selection.url)
      if (recorded) emitVoiceMessage(recorded)
      else showVoiceAttachError()
    }
  }

  /** Close teleported overlays before unmounting toolbar chrome (recorder swap). */
  const startVoiceRecordingFromMic = async () => {
    showToolsPopover.value = false
    showGifPicker.value = false
    showGifSheet.value = false
    showVideoUploadModal.value = false
    await nextTick()
    await voiceRecorder.start()
  }

  const handleAudioFilesSelected = async (files: FileSelection[]) => {
    showToolsPopover.value = false
    await emitVoiceFromFiles(files)
  }

  const handleMediaFilesSelected = async (files: MediaFile[]) => {
    const mediaItems = await Promise.all(
      files.map(async file => ({
        ...file,
        ...(await resolveMediaDimensions(file)),
        status: MessageStatus.PENDING,
      }))
    )

    mediaModel.value.push(...mediaItems)
    showToolsPopover.value = false
  }

  const handleVideoUrlImported = (videoData: { url: string; type: string; platform: string; isImported: boolean }) => {
    const uniqueId = `imported-${videoData.platform}-${Date.now()}`
    const importedVideo: EditorMediaItem = {
      _id: uniqueId,
      url: videoData.url,
      type: 'video' as const,
      name: `${videoData.platform} video`,
      size: 0,
      status: MessageStatus.COMPLETED,
      isImported: true,
      platform: videoData.platform,
      id: uniqueId,
    }

    mediaModel.value.push(importedVideo)
    ;(async () => applyOEmbedMeta(importedVideo, await fetchOEmbedMeta(videoData.url)))()
  }

  const handleDocumentFilesSelected = (files: FileSelection[]) => {
    const documentFiles = files.filter(file => !isVoiceNoteAudioFile(file))
    if (documentFiles.length === 0) {
      showToolsPopover.value = false
      return
    }

    const fileItems = documentFiles.map(file => ({
      ...file,
      status: MessageStatus.PENDING,
    }))

    filesModel.value.push(...fileItems)
    showToolsPopover.value = false
  }

  const handleDocumentsSelected = async (files: FileSelection[]) => {
    handleDocumentFilesSelected(files)
    await emitVoiceFromFiles(files.filter(file => isVoiceNoteAudioFile(file)))
  }

  const handleGifSelected = (gifData: { url: string; title: string }) => {
    const newGif: EditorGifMedia = {
      url: gifData.url,
      title: gifData.title,
      type: 'gif',
      id: `gif-${Date.now()}`,
      uploading: false,
      error: undefined,
    }
    gifsModel.value.push(newGif)
    showGifPicker.value = false
    showGifSheet.value = false
    showToolsPopover.value = false
  }

  const removeMediaItem = async (itemId: string) => {
    const index = mediaModel.value.findIndex(item => item._id === itemId)
    if (index === -1) return

    const item = mediaModel.value[index]

    if (item.status === MessageStatus.UPLOADING && currentUploadItem.value?._id === itemId) {
      try {
        await cancelUpload()
        currentUploadItem.value = null
        isUploading.value = false
      } catch (error) {
        console.error('Failed to cancel DRM upload:', error)
      }
    }

    revokeBlobUrlIfNeeded(item.url)

    mediaModel.value.splice(index, 1)

    if (currentUploadItem.value?._id === itemId) {
      currentUploadItem.value = null
      isUploading.value = false
    }
  }

  const handleRemoveImage = (index: number) => {
    const imageToRemove = getMediaItemByTypeAndIndex(mediaModel.value, 'image', index)
    if (imageToRemove) {
      removeMediaItem(imageToRemove._id)
    }
  }

  const handleRemoveVideo = (index: number) => {
    const videoToRemove = getMediaItemByTypeAndIndex(mediaModel.value, 'video', index)
    if (videoToRemove) {
      removeMediaItem(videoToRemove._id)
    }
  }

  const handleRemoveFile = (index: number) => {
    if (index >= 0 && index < filesModel.value.length) {
      const fileToRemove = filesModel.value[index]
      revokeBlobUrlIfNeeded(fileToRemove.url)
      filesModel.value.splice(index, 1)
    }
  }

  const handleRemoveGif = (index: number) => {
    if (index >= 0 && index < gifsModel.value.length) {
      const gifToRemove = gifsModel.value[index]
      revokeBlobUrlIfNeeded(gifToRemove.url)
      gifsModel.value.splice(index, 1)
    }
  }

  const handleFilesDropped = async (files: DroppedFile[]): Promise<RecordedVoiceMessage[]> => {
    const hasAudioDrop = files.some(file => isVoiceNoteAudioFile(file))
    const mediaFiles = files.filter(file => file.type.startsWith('image/') || file.type.startsWith('video/'))
    const audioFiles = files.filter(file => isVoiceNoteAudioFile(file))
    const docFiles = files.filter(file => !file.type.startsWith('image/') && !file.type.startsWith('video/') && !isVoiceNoteAudioFile(file))

    if (mediaFiles.length > 0) {
      const mediaWithTypes = mediaFiles.map(file => ({
        ...file,
        type: (file.type.startsWith('image/') ? 'image' : 'video') as 'image' | 'video',
      }))
      await handleMediaFilesSelected(mediaWithTypes)
    }

    if (docFiles.length > 0) {
      handleDocumentFilesSelected(docFiles)
    }

    const voiceMessages: RecordedVoiceMessage[] = []
    for (const audio of audioFiles) {
      if (!audio.file) continue
      const recorded = await attachUploadedVoiceForSend(audio.file, audio.url)
      if (recorded) {
        voiceMessages.push(recorded)
        emitVoiceMessage(recorded)
      }
    }

    // Only surface voice attach errors when the drop actually included audio —
    // otherwise a later image/doc drop can replay a stale size/duration toast.
    if (hasAudioDrop && !voiceMessages.length) showVoiceAttachError()

    return voiceMessages
  }

  const buildAttachments = (gifFallbackName: string): IChatAttachment[] => {
    const mediaAttachments = buildMediaAttachments(mediaModel.value)
    const gifAttachments = buildGifAttachments(gifsModel.value, gifFallbackName)
    const fileAttachments = buildFileAttachments(filesModel.value)

    return [...mediaAttachments, ...gifAttachments, ...fileAttachments]
  }

  const resetUploads = () => {
    mediaModel.value.forEach(media => {
      revokeBlobUrlIfNeeded(media.url)
    })
    filesModel.value.forEach(file => {
      revokeBlobUrlIfNeeded(file.url)
    })

    mediaModel.value = []
    gifsModel.value = []
    filesModel.value = []
    showToolsPopover.value = false
    showGifPicker.value = false
    showGifSheet.value = false
    showVideoUploadModal.value = false
    isUploading.value = false
    isFileUploading.value = false
    currentUploadItem.value = null
  }

  return {
    validations,
    imageCount,
    videoCount,
    fileCount,
    hasAttachments,
    hasUploadingMedia,
    imagesForCarousel,
    videosForCarousel,
    gifsForCarousel,
    filesForCarousel,
    showToolsPopover,
    showGifPicker,
    showGifSheet,
    showVideoUploadModal,
    voiceRecorderLabels,
    handleMediaFilesSelected,
    handleDocumentFilesSelected,
    handleDocumentsSelected,
    handleAudioFilesSelected,
    handleGifSelected,
    handleVideoUrlImported,
    handleRemoveImage,
    handleRemoveVideo,
    handleRemoveGif,
    handleRemoveFile,
    handleFilesDropped,
    buildAttachments,
    resetUploads,
    // Voice notes
    voiceRecorder,
    voiceStage: voiceRecorder.stage,
    voiceElapsedMs: voiceRecorder.elapsedMs,
    voiceAmplitudes: voiceRecorder.amplitudes,
    voiceError: voiceRecorder.error,
    startVoiceRecordingFromMic,
    stopVoiceRecordingForSend,
    attachUploadedVoiceForSend,
    lastVoiceAttachError,
    drmAppSource,
    drmEntityType,
    drmSourceEntityType,
    drmEntityId,
    drmSourceEntityId,
    DRMComponent,
    currentDRMFile,
    drmFileUploadRef,
    drmPlayRaw,
    handleDRMSuccess,
    handleDRMError,
    handleDRMProgress,
    handleDRMStatusChange,
  }
}
